import BaseComponent from "@bloglovin/inspire-engine/BaseComponent";
import App from "@bloglovin/inspire-engine/App";

import { Context } from "%/dynamic-component-editor/classes/context-v2";
import ProvidesContextEntry from "%/dynamic-component-editor/classes/provides-context-entry";
import { isProvidedByAncestor } from "%/dynamic-component-editor/helpers/entity-binding-problems";
import {
  PATH_MISSING_FIELD,
  PATH_NOT_A_REFERENCE,
  PATH_OK,
  referencedEntityConfig,
  resolveEntityFieldPath,
} from "%/dynamic-component-editor/helpers/entity-field-path";
import ElementRequiredContexts from "%/dynamic-component-editor/classes/element-required-contexts";
import ContextBindings from "%/dynamic-component-editor/options/context-bindings";
import { isDeclaredEmptyChain } from "%/dynamic-component-editor/helpers/declared-empty-chain";
import StopPropagation from "%/dynamic-component-editor/options/stop-propagation";
import { contextResolutionWarning }
  from "%/dynamic-component-editor/helpers/context-resolution-diagnostics";
import { resolveContextualAppName, resolveContextEngineAppName }
  from "%/dynamic-component-editor/classes/platform-standard-entities";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useDynamicComponentEditorStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-editor").default(...args);
// eslint-disable-next-line global-require
const useReadStatusStore = (...args) => require("%/dynamic-component-editor/stores/read-status-store").useReadStatusStore(...args);
// eslint-disable-next-line global-require
const useContinueActivityStore = (...args) => require("%/dynamic-component-editor/stores/continue-activity-store").useContinueActivityStore(...args);
const CONTINUE_STANDARD_ENTITIES = ["continueActivity", "continueProgram"];

// XXX: The ONE rule every context-bound element answers about itself, declared
// XXX: here so all of them inherit it rather than each element type repeating it.
// XXX: A binding names a context and a FIELD on that context's entity. If the
// XXX: entity does not declare that field the binding is DEAD DATA -- the value
// XXX: computeds below read `relatedEntity.fields[field].optional` and there is
// XXX: nothing to read. Reported as a problem so the element paints its own
// XXX: error block and the author can repoint or remove it; before this the read
// XXX: threw a bare TypeError that escaped into Vue's error handler (which then
// XXX: threw itself) and the WHOLE editor rendered nothing -- 0 canvas elements,
// XXX: 0 layers rows. See task #115.
// XXX: Resolved against the DC's OWN declared contexts on purpose: a component
// XXX: is authored against the contexts it declares, and an embedding page may
// XXX: legitimately satisfy the same context name with a different entity.
function contextBindingProblems({ element, dynamicComponent, contextEntities }) {
  // parsedOptions is the NORMALISED tree every consumer in the editor reads;
  // `element.options` is the raw value and is a different shape on a
  // DynamicElement (task #112 shipped that lesson).
  const binding = element.parsedOptions.context;
  const contextName = binding?.entityContext;
  const fieldName = binding?.field;
  if (!contextName || !fieldName) {
    return [];
  }
  const declaredContexts = dynamicComponent?.availableContextsEntities ?? [];
  const declared = declaredContexts.find(entry => entry?.name === contextName);
  if (!declared && isProvidedByAncestor(element, contextName)) {
    return [];
  }
  if (!declared) {
    return [
      `Bound to the context "${contextName}", which this ${dynamicComponent?.type ?? "component"} `
      + "does not provide. Re-point the binding in the inspector, or remove this element.",
    ];
  }
  const entityType = declared.entityType;
  if (!entityType) {
    return [];
  }
  const fields = contextEntities?.[entityType]?.fields;
  // No field map means the entity definitions have not loaded yet -- that is a
  // load ordering state, not a broken binding, and it re-evaluates when they land.
  if (!fields) {
    return [];
  }
  return fieldPathProblem({ fields, fieldName, entityType, contextEntities, contextName });
}

// A binding may walk a reference ("user.name"); helpers/entity-field-path.js
// owns that walk so the configuration check, the render-time read and the
// palette cannot disagree about what a path means.
function fieldPathProblem({ fields, fieldName, entityType, contextEntities, contextName }) {
  const resolved = resolveEntityFieldPath({
    fields,
    fieldName,
    entityType,
    lookupEntity: name => contextEntities?.[name],
  });
  if (resolved.status === PATH_MISSING_FIELD) {
    return [
      `Bound to "${contextName}"."${fieldName}", but the ${resolved.entityType} entity does not declare `
      + `"${resolved.segment}". It declares: ${Object.keys(resolved.fields).join(", ")}. `
      + "Re-pick the field in the inspector, or remove this element.",
    ];
  }
  if (resolved.status === PATH_NOT_A_REFERENCE) {
    return [
      `Bound to "${contextName}"."${fieldName}", but "${resolved.segment}" on ${resolved.entityType} is not `
      + "a reference to another entity, so the rest of the path cannot be read. "
      + "Re-pick the field in the inspector, or remove this element.",
    ];
  }
  return [];
}

const DynamicElementWithContext = {
  configurationProblems: contextBindingProblems,
  inject: {
    // Nearest embedding component's instance-scoped context bag (a live-reading
    // function; see ScopedProvidesContextsMixin). Scoped entries shadow page globals.
    scopedProvidedContexts: {
      from: "scopedProvidedContexts",
      default: null,
    },
    useExampleData: { default: () => () => false },
  },
  extends: BaseComponent,
  computed: {
    stopPropagation() {
      return this.getOptionValue(StopPropagation) === true;
    },
    isPreviewMode() {
      const fn = this.useExampleData;
      return typeof fn === "function" ? Boolean(fn()) : Boolean(fn);
    },
    elementContext() {
      // The binding persists under options.context (flat key beside the theme-mode
      // keys — see addElements); the bare .context fallbacks keep legacy shapes resolving.
      if (this.editMode) {
        const elementConfig = this.dynamicComponentInstance?.getElement(this.identifier);
        return elementConfig?.options?.context || elementConfig?.context || {};
      }
      // On the standalone production render a materialized-copy card's elements
      // live ONLY in the release blob — never loaded into the staging Element
      // store — so `dynamicComponentInstance.getElement()` returns null and the
      // element's context binding is lost (contextItem/relatedEntity resolve to
      // nothing → the element renders blank). DCPE already hands each element its
      // release-blob options tree as `optionValues`, and carryContextBinding
      // copies the flat `context` key into it, so read the binding from there
      // first. Falls back to the staging-derived config for non-production paths.
      const productionConfig = this.dynamicComponentInstance?.getElement(this.identifier);
      return this.optionValues?.context
        || productionConfig?.options?.context
        || this.configuration?.options?.context
        || this.configuration?.context
        || {};
    },
    // NOT `appName`: the engine's BaseComponent injects that, and shadowing an
    // inject with a computed warns on every element instance.
    contextAppName() {
      // On a render surface the builder appName resolves to the read-only contextual app.
      return resolveContextualAppName(this.elementContext.appName);
    },
    contextItems() {
      if (this.editMode) {
        return useDynamicComponentEditorStore().contextItems;
      }
      return { ...Context.allContexts, ...this.scopedProvidedContexts?.() ?? {} };
    },
    contextItem() {
      return this.contextItems[this.elementContext.entityContext];
    },
    entityType() {
      return this.contextItem?.entityType?.split(".").at(-1);
    },
    // Engine app for App.get (NOT contextAppName, which names the Context source):
    // "Legacy"/platform/un-stamped bindings resolve to the booted contextual app.
    resolvedAppName() {
      return resolveContextEngineAppName(this.elementContext.appName);
    },
    // Params an action sources from a context. The author binds one context per
    // row; the app's own key is an implementation detail they never see.
    elementRequiredContexts() {
      if (!this.elementContext?.actionName || !this.entityType) {
        return [];
      }
      const store = useDynamicComponentEditorStore();
      return ElementRequiredContexts.forAction(
        this.entityType,
        this.elementContext.appName,
        this.elementContext.actionName,
        store.allContextEntityConfigsByApp,
        store.allAvailableContextEntities,
      );
    },
    // The same `context-bindings` map a component element uses for its required
    // contexts — an action's requirements are bound through it too.
    elementContextBindings() {
      const bound = this.getOptionValue(ContextBindings);
      return bound && typeof bound === "object" ? bound : {};
    },
    relatedEntity() {
      if (this.editMode) {
        return useDynamicComponentEditorStore().allAvailableContextEntities[this.contextItem?.entityType];
      }

      // The app's Context source registers asynchronously in production — a
      // pre-registration render must return undefined, not crash the page subtree.
      const entityType = this.contextItem?.entityType;
      const source = Context.getSource(this.contextAppName);
      const fromAppSource = source?.entities?.[entityType];
      if (fromAppSource) {
        return fromAppSource;
      }
      // Legacy/host entities (Publication, Story, …) aren't in the consuming
      // app's Context source — resolve them from the shared "Legacy" Context
      // source, the authoritative legacyEntities payload, or (for the built-in
      // User backing currentUser) the standardUserEntity payload. Without this
      // a legacy/standard-context-bound element has no relatedEntity on the live
      // render → renders blank. Mirrors AppFeedComponentPicker's fallback.
      const fromLegacySource = Context.getSource("Legacy")?.entities?.[entityType];
      if (fromLegacySource) {
        return fromLegacySource;
      }
      const legacyDef = window.blclient?.data?.legacyEntities?.[entityType];
      if (legacyDef && legacyDef.fields) {
        return legacyDef;
      }
      const userDef = window.blclient?.data?.standardUserEntity;
      if (userDef && userDef.entityType === entityType && userDef.fields) {
        return userDef;
      }
      return undefined;
    },
    extractValuesFromContext() {
      return {
        default: this.defaultValue,
        exampleData: this.exampleData,
      };
    },
    // XXX: These three read the SAME field def, and all three used to do
    // XXX: `this.relatedEntity.fields[name].<prop>` behind a guard that checked
    // XXX: the entity existed and a field NAME was set -- never that the entity
    // XXX: DECLARES that field. A dangling binding therefore died on
    // XXX: `undefined.optional`: a bare TypeError carrying no message, which is
    // XXX: why the console named neither the element, the entity nor the field.
    // XXX: configurationProblems above catches this case first and the element
    // XXX: renders its own error block, so reaching here means the binding went
    // XXX: stale mid-render. Say exactly what is missing.
    boundFieldDefinition() {
      if (!this.relatedEntity || !this.elementContext.field) {
        return null;
      }
      const fieldName = this.elementContext.field;
      const resolved = resolveEntityFieldPath({
        fields: this.relatedEntity.fields,
        fieldName,
        entityType: this.relatedEntity.entityType ?? "bound",
        lookupEntity: referencedEntityConfig,
      });
      if (resolved.status === PATH_OK) {
        return resolved.field;
      }
      // A hop the registry cannot answer yet is a load state; the engine still
      // reads the value, and there is no definition to hand back.
      if (resolved.status !== PATH_MISSING_FIELD && resolved.status !== PATH_NOT_A_REFERENCE) {
        return null;
      }
      throw new Error(
        `Element ${this.identifier} is bound to "${this.elementContext.entityContext}"."${fieldName}", `
        + `but the ${resolved.entityType} entity does not declare "${resolved.segment}". `
        + `It declares: ${Object.keys(resolved.fields ?? {}).join(", ")}.`,
      );
    },
    exampleData() {
      return this.boundFieldDefinition?.exampleData;
    },
    defaultValue() {
      return this.boundFieldDefinition?.default;
    },
    isOptional() {
      return this.boundFieldDefinition?.optional;
    },
    valueFromContext: {
      get() {
        if (this.editMode || this.isPreviewMode) {
          if (this.isOptional) {
            return this.extractValuesFromContext.exampleData;
          }
          return this.extractValuesFromContext.default ?? this.extractValuesFromContext.exampleData;
        }
        if (!this.contextItem || !this.entityType) {
          return undefined; // unresolved context (see created's production note)
        }
        const entityPk = this.contextItem.entityPkUrlVars;
        // A pk-less read 500s server-side ("Entity Missing primary key field") —
        // degrade to undefined; the value re-resolves reactively once a real pk lands.
        const hasPk = entityPk && typeof entityPk === "object"
          ? Object.values(entityPk).some(value => value !== undefined && value !== null && value !== "")
          : Boolean(entityPk);
        if (!hasPk) {
          return undefined;
        }
        const app = App.get(this.resolvedAppName);
        const entityType = this.entityType;
        const fieldName = this.elementContext.field;

        return app.getEntityField(
          entityType,
          entityPk,
          fieldName,
        );
      },
      set(newValue) {
        if (!this.contextItem || !this.entityType) {
          return;
        }
        const app = App.get(this.resolvedAppName);
        const entityType = this.entityType;
        const entityPk = this.contextItem.entityPkUrlVars;
        const fieldName = this.elementContext.field;
        const params = {
          entityType,
          entityPk,
          fields: { [fieldName]: newValue },
        };
        const appName = this.resolvedAppName;
        if (app.getEntityFieldDef(entityType, fieldName, appName)?.stateOnly) {
          app.triggerEventWithoutSendingToServer("UpdateEntityField", params, appName);
          return;
        }
        app.triggerEvent("UpdateEntityField", params, appName);
      },
    },
  },
  data() {
    return {
      actionAwaitingContext: null,
    };
  },
  watch: {
    contextItem(item) {
      if (!item || !this.actionAwaitingContext) {
        return;
      }
      const actionName = this.actionAwaitingContext;
      this.actionAwaitingContext = null;
      this.triggerNamedAction(actionName);
    },
  },
  created() {
    if (this.editMode) {
      // NEVER throw on a missing/unresolved context in edit mode: a throw aborts the
      // embedding component's mount too, breaking sibling elements' type/layout resolution.
      const warning = contextResolutionWarning({
        boundContextName: this.elementContext?.entityContext,
        boundField: this.elementContext?.field,
        hasContextItem: Boolean(this.contextItem),
        hasRelatedEntity: Boolean(this.relatedEntity),
        identifier: this.identifier,
      });
      if (warning) {
        // eslint-disable-next-line no-console
        console.warn(warning);
      }
      return;
    }
    if (this.isPreviewMode) {
      return;
    }
    // The ambient resume context resolves to no pk at bootstrap on purpose — it
    // costs a request most pages never need. Binding it is the signal to fetch.
    if (this.contextItem?.standardEntity === ProvidesContextEntry.KEY_STANDARD_RESUME_STORY) {
      useReadStatusStore().loadResumeStory();
    }
    if (CONTINUE_STANDARD_ENTITIES.includes(this.contextItem?.standardEntity)) {
      useContinueActivityStore().loadContinueTarget();
    }
    if (!this.contextItem && isDeclaredEmptyChain(this.elementContext?.entityContext, Context.rawContexts)) {
      return;
    }
    if (!this.elementContext || !this.contextItem || !this.relatedEntity) {
      const contextName = this.elementContext?.entityContext ?? "";
      const scopedNames = Object.keys(this.scopedProvidedContexts?.() ?? {});
      const pageNames = Object.keys(Context.allContexts ?? {});
      // eslint-disable-next-line no-console
      console.warn(
        `DynamicElementWithContext: element ${this.identifier} is bound to context "${contextName || "(none)"}", `
        + `which ${this.contextItem ? "names no loaded entity" : "is not provided here"} `
        + `(page contexts: ${pageNames.join(", ") || "none"}; scoped contexts: ${scopedNames.join(", ") || "none"}) `
        + "— element renders empty. If a component embed or completion card should hand it this context, "
        + "bind the card's required context on the element that renders it.",
        {
          identifier: this.identifier,
          contextName,
          elementContext: this.elementContext,
          pageContextNames: pageNames,
          scopedContextNames: scopedNames,
          hasContextItem: Boolean(this.contextItem),
          hasRelatedEntity: Boolean(this.relatedEntity),
        },
      );
    }
  },
  methods: {
    siblingFieldValue(fieldName) {
      if (!fieldName || !this.contextItem || !this.entityType) {
        return undefined;
      }
      const entityPk = this.contextItem.entityPkUrlVars;
      const hasPk = entityPk && typeof entityPk === "object"
        ? Object.values(entityPk).some(value => value !== undefined && value !== null && value !== "")
        : Boolean(entityPk);
      if (!hasPk) {
        return undefined;
      }
      try {
        return App.get(this.resolvedAppName).getEntityField(this.entityType, entityPk, fieldName);
      } catch {
        return undefined;
      }
    },
    valueFromNamedContext(reference) {
      const [contextName, ...fieldPath] = String(reference ?? "").split(".");
      const fieldName = fieldPath.join(".");
      if (!contextName || !fieldName) {
        return undefined;
      }
      const item = this.contextItems[contextName];
      const entityType = item?.entityType?.split(".").at(-1);
      if (!item || !entityType || !item.entityPkUrlVars) {
        return undefined;
      }
      try {
        return App.get(this.resolvedAppName).getEntityField(entityType, item.entityPkUrlVars, fieldName);
      } catch (e) {
        return undefined;
      }
    },

    executeAction(event) {
      if (this.stopPropagation && event) {
        event.stopPropagation();
      }
      this.triggerNamedAction(this.elementContext.actionName);
    },

    triggerNamedAction(actionName) {
      if (this.editMode || !actionName) {
        return;
      }
      if (!this.contextItem || !this.entityType) {
        this.actionAwaitingContext = actionName;
        return;
      }
      this.actionAwaitingContext = null;

      const app = App.get(this.resolvedAppName);
      const entityType = this.entityType;
      const entityPk = this.contextItem.entityPkUrlVars;
      const entity = app.getEntity(entityType, entityPk);

      // Contexts feed action-param resolution then stop (the event sees only
      // eventParams); fire only via the engine Entity's public triggerAction.
      const scopedActionContexts = Object.fromEntries(
        Object.entries(this.scopedProvidedContexts?.() ?? {}).map(([contextName, item]) => [
          contextName,
          { entityType: item.entityType, pk: item.entityPkUrlVars },
        ]),
      );
      const required = this.elementRequiredContexts;
      const bindings = this.elementContextBindings;
      const inScope = { ...Context.contexts, ...scopedActionContexts };
      const unbound = ElementRequiredContexts.unbound(required, bindings, inScope);
      if (unbound.length) {
        // Firing with a requirement nobody answered resolves it to nothing and
        // writes a half-formed event. Name what is missing instead.
        // eslint-disable-next-line no-console
        console.error(
          `Action "${actionName}" needs a context bound to ${unbound.join(", ")} `
          + "— bind it beside this element's binding. Nothing was fired.",
        );
        return;
      }
      entity.triggerAction(
        actionName,
        ElementRequiredContexts.applyBindings(inScope, required, bindings),
      );
    },
  },
};

export default DynamicElementWithContext;
